import { useEffect, useRef, useState } from 'react';

import { LogicOperator } from '@/types/logic';
import type { LogicElement, LogicGroup } from '@/types/logic';
import type { NewLogicElementDefaults, QueryBuilderProps } from '@/ui/QueryBuilder';

import { useLogicApplication, useLogicFieldOptions } from './useLogicApplication';

/**
 * A no-mutation counterpart to `useLogicQueryBuilder`: it drives the generic
 * `<QueryBuilder />` from purely local React state instead of persisting each
 * edit to the API. Field options are fetched by application key alone (no
 * subject needed), so it works for a brand-new subject that doesn't exist yet.
 * The current `rootGroups` are returned for the form to serialize into its own
 * submit payload; nothing here touches the server.
 */
const makeEmptyGroup = (id: string): LogicGroup => ({
  id,
  logic_operator: LogicOperator.AND,
  // subject_id/logic_application are assigned server-side on save; the builder
  // never sends them, so a placeholder is fine for local state.
  subject_id: '',
  logic_elements: [],
  logic_groups: []
});

function mapGroups(
  groups: LogicGroup[],
  groupId: string,
  update: (group: LogicGroup) => LogicGroup
): LogicGroup[] {
  return groups.map((group) =>
    group.id === groupId
      ? update(group)
      : { ...group, logic_groups: mapGroups(group.logic_groups, groupId, update) }
  );
}

function removeGroup(groups: LogicGroup[], groupId: string): LogicGroup[] {
  return groups
    .filter((group) => group.id !== groupId)
    .map((group) => ({ ...group, logic_groups: removeGroup(group.logic_groups, groupId) }));
}

function findGroupContainingElement(groups: LogicGroup[], elementId: string): LogicGroup | null {
  for (const group of groups) {
    if (group.logic_elements.some((element) => element.id === elementId)) return group;
    const found = findGroupContainingElement(group.logic_groups, elementId);
    if (found) return found;
  }
  return null;
}

const useLocalLogicQueryBuilder = ({
  applicationKey,
  initialRootGroups
}: {
  applicationKey: string;
  initialRootGroups?: LogicGroup[];
}) => {
  const { data: application, isLoading: isLoadingApplication } =
    useLogicApplication(applicationKey);
  const applicationId = application?.id;
  const { data: fieldOptions, isLoading: isLoadingFieldOptions } =
    useLogicFieldOptions(applicationId);

  const [rootGroups, setRootGroups] = useState<LogicGroup[]>(initialRootGroups ?? []);

  // Existing criteria load asynchronously on edit; seed local state the first
  // time they arrive without clobbering later edits if the query refetches.
  const seededRef = useRef(initialRootGroups !== undefined);
  useEffect(() => {
    if (!seededRef.current && initialRootGroups !== undefined) {
      setRootGroups(initialRootGroups);
      seededRef.current = true;
    }
  }, [initialRootGroups]);

  // Temp ids are only React keys / callback routing handles; they are dropped
  // when the tree is serialized, so a plain counter is enough.
  const idCounterRef = useRef(0);
  const nextId = (prefix: string) => `${prefix}-${++idCounterRef.current}`;

  const onAddRootGroup = (): string => {
    const id = nextId('group');
    setRootGroups((groups) => [...groups, makeEmptyGroup(id)]);
    return id;
  };

  const onAddChildGroup: QueryBuilderProps['onAddChildGroup'] = (parentGroupId) =>
    setRootGroups((groups) =>
      mapGroups(groups, parentGroupId, (group) => ({
        ...group,
        logic_groups: [...group.logic_groups, makeEmptyGroup(nextId('group'))]
      }))
    );

  const onUpdateGroup: QueryBuilderProps['onUpdateGroup'] = (groupId, data) =>
    setRootGroups((groups) => mapGroups(groups, groupId, (group) => ({ ...group, ...data })));

  const onDeleteGroup: QueryBuilderProps['onDeleteGroup'] = (groupId) =>
    setRootGroups((groups) => removeGroup(groups, groupId));

  const onAddElement = (groupId: string, defaults: NewLogicElementDefaults) => {
    const element: LogicElement = {
      id: nextId('element'),
      logic_group: groupId,
      is_negated: false,
      ...defaults
    };
    setRootGroups((groups) =>
      mapGroups(groups, groupId, (group) => ({
        ...group,
        logic_elements: [...group.logic_elements, element]
      }))
    );
  };

  const onChangeElement: QueryBuilderProps['onChangeElement'] = (elementId, data) =>
    setRootGroups((groups) => {
      const owningGroup = findGroupContainingElement(groups, elementId);
      if (!owningGroup) return groups;
      return mapGroups(groups, owningGroup.id, (group) => ({
        ...group,
        logic_elements: group.logic_elements.map((element) =>
          element.id === elementId ? { ...element, ...data } : element
        )
      }));
    });

  const onDeleteElement: QueryBuilderProps['onDeleteElement'] = (elementId) =>
    setRootGroups((groups) => {
      const owningGroup = findGroupContainingElement(groups, elementId);
      if (!owningGroup) return groups;
      return mapGroups(groups, owningGroup.id, (group) => ({
        ...group,
        logic_elements: group.logic_elements.filter((element) => element.id !== elementId)
      }));
    });

  return {
    rootGroups,
    fieldOptions: fieldOptions ?? [],
    isLoading: isLoadingApplication || isLoadingFieldOptions,
    onAddRootGroup,
    onAddChildGroup,
    onUpdateGroup,
    onDeleteGroup,
    onAddElement,
    onChangeElement,
    onDeleteElement
  };
};

export default useLocalLogicQueryBuilder;
